import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';

import connectUI from '../../basics/ui/UIConnector';
import suitcss from '../../../helpers/suitcss';
import { shape as tariffShape } from '../../../propTypes/tariff';
import Headline from '../../basics/text/TextHeadline';
import Copy from '../../basics/text/TextCopy';
import Unit from '../../basics/text/TextUnit';
import { getTariffDuration } from '../../../helpers/entity';
import Callout from '../../../containers/callout/Callout';
import FootnoteReference from '../../../containers/footnote/FootnoteReference';
import TextLink from '../../basics/text/TextLink';

class TariffItemBox extends PureComponent {

  constructor(...args) {
    super(...args);
    this.state = {};
  }

  renderHeader() {
    const {
      tariff,
      moduleId,
      withEqualizedHeader,
      linkPrimary,
      featureSet,
      ui,
    } = this.props;

    const { headline } = tariff;
    const isFeatureSetInfo = featureSet === 'info';
    const equalizerGroup = `${moduleId}-tariffItemFullHeader`;
    const equalizerData = withEqualizedHeader
      ? { 'data-eq-group-l': equalizerGroup, 'data-eq-group-m': equalizerGroup, 'data-eq-group-s': equalizerGroup }
      : {};
    const linkProps = isFeatureSetInfo
      ? {}
      : {
        to: linkPrimary.to,
        onClick: linkPrimary.onClick,
      };
    const tariffType = tariff.type;

    return (
      <div
        className={suitcss({
          descendantName: 'header',
        }, this)}
        {...equalizerData}
        {...linkProps}
      >
        <Headline
          className={'overline-copy'}
          embedded
          raw
          utilities={['alignCenter', 'colorDefault']}
        >
          {tariffType}
        </Headline>
        <Headline
          className={'headline-copy'}
          embedded
          raw
          utilities={['colorPrimary', 'weightBold', 'inlineBlock', 'headlineXXXL']}
        >
          {headline}
        </Headline>
        <Headline
          className={'headline-unit'}
          embedded
          raw
          utilities={['colorPrimary', 'weightBold', 'inlineBlock', 'headlineXXXL']}
        >
          {ui.guiSymbolGigabyte}
        </Headline>
      </div>
    );
  }

  renderContent() {
    const { tariff, inverted } = this.props;

    return (
      <div
        className={suitcss({
          descendantName: 'content',
        }, this)}
      >
        <div
          className={suitcss({
            descendantName: 'priceMonthly',
          }, this)}
        >
          <div
            className={suitcss({
              descendantName: 'unit',
              utilities: ['marginBottomS'],
            }, this)}
          >
            <Unit
              postfix={[
                <FootnoteReference id={tariff.eid} />,
              ]}
              price={tariff.paymentFee}
              prefixPrice={tariff.paymentFeeStrike}
              inverted={inverted}
              modifiers={[
                inverted && 'colorInverted',
              ]}
              priceTheme={'tertiary'}
            />
          </div>
        </div>
      </div>
    );
  }

  renderSubContent(isFeatureSetInfo) {
    const { tariff } = this.props;
    const docs = isFeatureSetInfo && tariff.documents ? tariff.documents : null;

    return (
      <div
        className={suitcss({
          descendantName: 'subcontent',
          utilities: ['marginBottomS'],
        }, this)}
      >
        {isFeatureSetInfo && docs && (
          <Copy
            element="div"
            utilities={['textSizeSmall', 'alignLeft']}
            embedded
          >
            {docs.map((doc, index) => (
              <p className={suitcss({ utilities: [index > 0 && 'marginTopXXS'] })} key={index}>
                <a href={doc.file.url} target="_blank" rel="noopener noreferrer" >
                  {doc.title}
                </a>
              </p>
            ))}
          </Copy>
        )}
      </div>
    );
  }

  renderTariffDuration() {
    const { ui, tariff } = this.props;
    return (
      <div className={suitcss({ descendantName: 'tariffDuration' }, this)}>
        <div className={suitcss({ descendantName: 'tariffDuration' }, this)}>
          {getTariffDuration(tariff, ui)}
        </div>
        {this.renderProductInformations()}
      </div>
    );
  }

  renderProductInformations() {
    const { productInformationSheetLink } = this.props;

    const onClick = (ev) => {
      ev.stopPropagation();
    };

    return (
      <TextLink
        className={suitcss({ descendantName: 'productInformation' }, this)}
        href={productInformationSheetLink.file.url}
        withoutArrow
        element="a"
        onClick={onClick}
        target={'_blank'}
      >
        {productInformationSheetLink.copy}
      </TextLink>
    );
  }

  render() {
    const {
      tariff,
      moduleId,
      featureSet,
      isMediaS,
      isMediaSM,
      themeBoxedCallback,
      isActive,
      inverted,
      colorScheme,
      hasLabelCallouts,
      isMultiRowSM,
      isUpperRow,
      isLowerRow,
      upperRowHasLabelCallout,
      lowerRowHasLabelCallout,
    } = this.props;
    const { iid } = tariff;
    const isFeatureSetInfo = featureSet === 'info';
    const equalizerGroupUpper = `${moduleId}1-tariffItemFullContent`;
    const equalizerGroupLower = `${moduleId}2-tariffItemFullContent`;
    const equalizerGroup = `${moduleId}-tariffItemFullContent`;
    const equalizerData = isMultiRowSM
      ? {
        'data-eq-group-l': equalizerGroup,
        'data-eq-group-m': isUpperRow ? equalizerGroupUpper : equalizerGroupLower,
        'data-eq-group-s': isUpperRow ? equalizerGroupUpper : equalizerGroupLower,
      }
      : { 'data-eq-group-l': equalizerGroup, 'data-eq-group-m': equalizerGroup, 'data-eq-group-s': equalizerGroup };
    const rowWithCallout =
      (isUpperRow && upperRowHasLabelCallout)
      || (isLowerRow && lowerRowHasLabelCallout);
    return (
      <section
        onClick={themeBoxedCallback}
        className={suitcss({
          modifiers: [isActive && 'activeItem', colorScheme],
          utilities: [((hasLabelCallouts && !isMediaS) || (isMediaSM && rowWithCallout)) && 'marginTop'],
        }, this)}
        data-tracking="tariff"
        data-tariff-id={iid}
        {...equalizerData}
      >
        <Callout
          inverted={inverted}
          theme="sticker"
          targets={[tariff.eid]}
        />
        {this.renderHeader()}
        <Callout
          theme="content"
          targets={[tariff.eid]}
          inverted={inverted}
        />
        <div className={suitcss({ descendantName: 'main' }, this)}>
          {this.renderContent()}
          {this.renderTariffDuration()}
          {!isMediaS && this.renderSubContent(isFeatureSetInfo)}
        </div>
      </section>
    );
  }
}

TariffItemBox.propTypes = {
  colorScheme: PropTypes.oneOf(['dark', 'light']),
  tariff: tariffShape.isRequired,
  themeBoxedCallback: PropTypes.func,
  isActive: PropTypes.bool,
  inverted: PropTypes.bool,
  linkPrimary: PropTypes.shape({
    label: PropTypes.string,
    to: PropTypes.string,
    onClick: PropTypes.func,
  }).isRequired,
  urlSelect: PropTypes.string,
  moduleId: PropTypes.string,
  featureSet: PropTypes.string,
  withEqualizedHeader: PropTypes.bool,
  isMediaS: PropTypes.bool,
  isMediaSM: PropTypes.bool,
  ui: PropTypes.shape({}),
  hasLabelCallouts: PropTypes.bool,
  isMultiRowSM: PropTypes.bool,
  isUpperRow: PropTypes.bool,
  isLowerRow: PropTypes.bool,
  upperRowHasLabelCallout: PropTypes.bool,
  lowerRowHasLabelCallout: PropTypes.bool,
  productInformationSheetLink: PropTypes.object,
};

TariffItemBox.defaultProps = {
  inverted: true,
  featureSet: 'full',
  withEqualizedHeader: true,
};

export default connectUI()(TariffItemBox);
